import React, { useState, useEffect, useMemo, useRef, createRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "react-datepicker/dist/react-datepicker.css";
import {
  PlusCircle,
  Edit2,
  BarChart3,
  TrendingUp,
  MapPin,
  PieChart as PieChartIcon,
  Table,
  Move,
  LineChart as LineChartIcon,
  AreaChart as AreaChartIcon,
  Search,
  Trash,
  BarChart4,
  ChartBarStacked,
  CircleDashed,
  Radar as RadarIcon,
} from "lucide-react";
import { Scatter, Funnel } from "recharts";
import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import { getData } from "../../../Services/Services";
import {
  getQueriesListByWidgetType,
  getThemeURL,
} from "../../../Services/EndPoints";
import LineChartComponent from "./CreateWidget/Charts/LineChart";
import { TrendChart } from "./CreateWidget/Charts/TrendChart";
import StackedBarChartComponent from "./CreateWidget/Charts/StackedBarChart";
import ComposedChartComponent from "./CreateWidget/Charts/ComposedBarChart";
import RadarChartComponent from "./CreateWidget/Charts/RadarChartComponent";
import RadialProgressChart from "./CreateWidget/Charts/RadialProgressChart";
import ProgressBarChart from "./CreateWidget/Charts/ProgressBarChart";
import AreaChartJS from "./CreateWidget/Charts/AreaChart";
import PieChartJS from "./CreateWidget/Charts/PieChart";
import BarChart from "./CreateWidget/Charts/BarChart";
import Map from "./CreateWidget/Map/Map";
import CardComponent from "./CreateWidget/Card/CardComponent";
import TableComponent from "./CreateWidget/Table/TableComponent";
import TreeListComponent from "./CreateWidget/Card/BankListComponent";

const DEFAULT_WIDGET_SIZES = {
  card: { w: 4, h: 5 },
  table: { w: 5, h: 15 },
  chart: { w: 5, h: 15 },
  map: { w: 15, h: 20 },
  HO_bank_list: { w: 5, h: 15 },
};

const GridLayoutCards = ({
  items,
  handleEdit,
  handleDelete,
  handleDuplicate,
  isEditingLayout,
  onGridUpdate,
  renderIcon,
  handleRestore,
  tablePagination,
  onTablePageChange,
  onTableRowsPerPageChange,
  uploadedIcons,
  iconSizes,
  isViewMode,
  searchTerm,
  originalItemsCount,
  selectedBankId,
  onBankSelect,
  selectedBranchId,
  onBranchSelect,
  selectedBankIds,
  selectedBranchIds,
  handleMultiBankSelect,
  handleMultiBranchSelect,
  attachmentList,
  editConfigIds,
}) => {
  const refs = useRef({});
  const gridRef = useRef();
  const [gridChanged, setGridChanged] = useState(false);
  console.log(setGridChanged, "setGridChanged");
  const showNoWidgetsFound =
    searchTerm &&
    searchTerm.trim() !== "" &&
    items.length === 0 &&
    originalItemsCount > 0;

  const processedItems = useMemo(() => {
    return items.map((item) => {
      if (item.w && item.h) return item;

      const defaults = DEFAULT_WIDGET_SIZES[item.type] || { w: 3, h: 2 };
      return {
        ...item,
        w: item.w || defaults.w,
        h: item.h || defaults.h,
      };
    });
  }, [items]);

  const currentItemIds = new Set(processedItems.map((item) => item.id));

  // Remove refs for deleted items
  Object.keys(refs.current).forEach((id) => {
    if (!currentItemIds.has(parseInt(id))) {
      delete refs.current[id];
    }
  });

  // Add refs for new items
  processedItems.forEach(({ id }) => {
    if (!refs.current[id]) {
      refs.current[id] = createRef();
    }
  });

  useEffect(() => {
    if (!gridRef.current) {
      gridRef.current = GridStack.init(
        {
          cellHeight: 20,
          resizable: { handles: "all" },
          acceptWidgets: true,
          removable: false,
          column: 12,
          minRow: 1,
          margin: 0,
          animate: false,
          float: true,
          disableOneColumnMode: true,
          dragIn: false,
          dragInOptions: { revert: "invalid", scroll: false },
          disableDrag: isViewMode,
          disableResize: isViewMode,
        },
        ".grid-stack-container"
      );
    } else {
      // Update existing grid options if grid already exists
      const grid = gridRef.current;
      grid.opts.disableDrag = isViewMode;
      grid.opts.disableResize = isViewMode;
    }

    const grid = gridRef.current;
    grid.batchUpdate();
    grid.removeAll(false);

    let maxY = 0;
    processedItems.forEach((item) => {
      if (item.y + (item.h || 1) > maxY) {
        maxY = item.y + (item.h || 1);
      }
    });

    processedItems.forEach((item) => {
      const el = refs.current[item.id]?.current;
      if (el) {
        if (item.x === undefined || item.y === undefined) {
          item.x = 0;
          item.y = maxY;
          maxY += item.h || 1;
        }
        el.setAttribute("gs-id", item.id);
        el.setAttribute("gs-x", item.x);
        el.setAttribute("gs-y", item.y);
        el.setAttribute("gs-w", item.w || 3);
        el.setAttribute("gs-h", item.h || 2);

        grid.makeWidget(el);
      }
    });

    grid.batchUpdate(false);

    const saveLayout = () => {
      // Only save layout if dragging/resizing is enabled
      if (!isViewMode) {
        grid.engine.nodes.forEach((node) => {
          const id = parseInt(node.el.getAttribute("gs-id"));
          if (!id) return;
          handleEdit(id, "x", node.x);
          handleEdit(id, "y", node.y);
          handleEdit(id, "w", node.w);
          handleEdit(id, "h", node.h);
        });
      }
    };

    grid.on("change", saveLayout);
    grid.on("resizestop", saveLayout);
    grid.on("dragstop", saveLayout);

    return () => {
      grid.off("change", saveLayout);
      grid.off("resizestop", saveLayout);
      grid.off("dragstop", saveLayout);
    };
  }, [
    gridChanged,
    processedItems.length,
    processedItems,
    handleEdit,
    isViewMode,
  ]);

  useEffect(() => {
    return () => {
      if (gridRef.current) {
        gridRef.current.destroy(false);
        gridRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (showNoWidgetsFound) {
    return (
      <div
        className="grid-stack grid-stack-container"
        style={{
          minHeight: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          backgroundColor: "#f9fafb",
          borderRadius: "12px",
          border: "2px dashed #d1d5db",
        }}
      >
        <div
          style={{
            textAlign: "center",
            color: "#6b7280",
            fontSize: "18px",
            fontWeight: "500",
            padding: "40px 20px",
          }}
        >
          <Search size={48} color="#9ca3af" style={{ marginBottom: "16px" }} />
          <div
            style={{ marginBottom: "8px", fontSize: "20px", fontWeight: "600" }}
          >
            No Widgets Found
          </div>
          <div style={{ fontSize: "14px" }}>
            No widgets match your search term "{searchTerm}".
            <br />
            Try adjusting your search or clear the search to see all widgets.
          </div>
        </div>
      </div>
    );
  }

  const fetchUserProfile = async () => {
    try {
      const response = await getData(getThemeURL);
      if (Array.isArray(response) && response.length > 0) {
        console.log("fetched USER", response);
        if (response[0].is_admin === true) {
          console.log("FILTERED ITEMS", items);
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  return (
    <div
      className="grid-stack grid-stack-container"
      style={{
        minHeight: "400px",
        cursor: isViewMode ? "default" : "move",
      }}
    >
      {processedItems.map((item) => (
        <GridStackCard
          key={item.id}
          item={item}
          ref={refs.current[item.id]}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
          isEditingLayout={isEditingLayout}
          renderIcon={renderIcon}
          handleRestore={handleRestore}
          tablePagination={tablePagination[item.id]?.page || 1}
          tableRowsPerPage={tablePagination[item.id]?.rowsPerPage || 5}
          onTablePageChange={(newPage) => onTablePageChange(item.id, newPage)}
          onTableRowsPerPageChange={(newRows) =>
            onTableRowsPerPageChange(item.id, newRows)
          }
          uploadedIcons={uploadedIcons}
          iconSizes={iconSizes}
          isViewMode={isViewMode}
          selectedBankId={selectedBankId}
          onBankSelect={onBankSelect}
          selectedBranchId={selectedBranchId}
          onBranchSelect={onBranchSelect}
          selectedBankIds={selectedBankIds}
          selectedBranchIds={selectedBranchIds}
          handleMultiBankSelect={handleMultiBankSelect}
          handleMultiBranchSelect={handleMultiBranchSelect}
          attachmentList={attachmentList}
          editConfigId={editConfigIds}
        />
      ))}
    </div>
  );
};

const GridStackCard = React.forwardRef(
  (
    {
      item,
      handleEdit,
      handleDelete,
      handleDuplicate,
      isEditingLayout,
      uploadedIcons,
      iconSizes,
      isViewMode,
      tablePagination,
      tableRowsPerPage,
      onTablePageChange,
      onTableRowsPerPageChange,
      selectedBankId,
      onBankSelect,
      selectedBranchId,
      onBranchSelect,
      selectedBankIds,
      selectedBranchIds,
      handleMultiBankSelect,
      handleMultiBranchSelect,
      attachmentList,
      editConfigId,
    },
    ref
  ) => {
    const [editingId, setEditingId] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandLevel, setExpandLevel] = useState(item.expandLevel || 1);
    // const [heightExpandLevel, setHeightExpandLevel] = useState(
    //   item.heightExpandLevel || 1,
    // );
    // const [sortConfig, setSortConfig] = useState({
    //   key: null,
    //   direction: "ascending",
    // });
    console.log(editingId, setIsExpanded, setExpandLevel);
    const [expandedBanks, setExpandedBanks] = useState(new Set());

    // MOVED HOOKS FROM RENDER FUNCTIONS TO PARENT COMPONENT
    // Table-related hooks
    const [tableSavedQueries, setTableSavedQueries] = useState([]);
    const [tableLoadingQueries, setTableLoadingQueries] = useState(false);
    const [tableQueryError, setTableQueryError] = useState(null);

    // Card-related hooks
    const [cardSavedQueries, setCardSavedQueries] = useState([]);
    const [cardLoadingQueries, setCardLoadingQueries] = useState(false);
    const [cardQueryError, setCardQueryError] = useState(null);

    // Combined useEffect for fetching queries based on widget type
    useEffect(() => {
      const hasFilters =
        selectedBankIds?.length > 0 || selectedBranchIds?.length > 0;

      if (hasFilters) {
        // Fetch queries based on widget type
        if (item.type === "table") {
          const fetchTableQueries = async () => {
            try {
              setTableLoadingQueries(true);
              setTableQueryError(null);
              const response = await getData(
                getQueriesListByWidgetType.replace("WIDGETTYPE", "TABLE")
              );

              const parsed = response.map((queryItem) => ({
                ...queryItem,
                queryBuilder:
                  typeof queryItem.queryBuilder === "string"
                    ? JSON.parse(queryItem.queryBuilder)
                    : queryItem.queryBuilder,
              }));

              setTableSavedQueries(parsed);
            } catch (err) {
              console.error("Error fetching table queries:", err);
              setTableQueryError("Failed to load saved queries");
            } finally {
              setTableLoadingQueries(false);
            }
          };

          fetchTableQueries();
        } else if (item.type === "card") {
          const fetchCardQueries = async () => {
            try {
              setCardLoadingQueries(true);
              setCardQueryError(null);
              const response = await getData(
                getQueriesListByWidgetType.replace("WIDGETTYPE", "CARD")
              );

              const parsed = response.map((queryItem) => ({
                ...queryItem,
                queryBuilder:
                  typeof queryItem.queryBuilder === "string"
                    ? JSON.parse(queryItem.queryBuilder)
                    : queryItem.queryBuilder,
              }));

              setCardSavedQueries(parsed);
            } catch (err) {
              console.error("Error fetching card queries:", err);
              setCardQueryError("Failed to load saved queries");
            } finally {
              setCardLoadingQueries(false);
            }
          };

          fetchCardQueries();
        }
      }
    }, [selectedBankIds, selectedBranchIds, item.type]);

    const iconMap = {
      "Atm's": new L.Icon({
        iconUrl: "https://cdn-icons-png.flaticon.com/512/10410/10410410.png",
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      }),
      "Bank's": new L.Icon({
        iconUrl: "https://cdn-icons-png.flaticon.com/512/10410/10410388.png",
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      }),
      Incidents: new L.Icon({
        iconUrl: "https://cdn-icons-png.flaticon.com/512/564/564619.png",
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      }),
      "Cash vans": new L.Icon({
        iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854894.png",
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      }),
      default: new L.Icon.Default(),
    };

    // First layer - renderChartContent (similar to renderCardContent and renderTableContent)
    const renderChartContent = (item) => {
      return (
        <ChartRenderer
          item={item}
          selectedBankIds={selectedBankIds}
          selectedBranchIds={selectedBranchIds}
          isExpanded={isExpanded}
          isViewMode={isViewMode}
        />
      );
    };

    // Second layer - ChartRenderer (handles async data fetching)
    const ChartRenderer = ({
      item,
      selectedBankIds,
      selectedBranchIds,
      isExpanded,
      isViewMode,
    }) => {
      const [chartData, setChartData] = useState([]);
      const [loading, setLoading] = useState(false);
      const [savedQueries, setSavedQueries] = useState([]);
      const [loadingQueries, setLoadingQueries] = useState(false);
      const [queryError, setQueryError] = useState(null);
      const containerRef = useRef();

      console.log("ChartRenderer - selectedBankIds:", selectedBankIds);
      console.log("ChartRenderer - selectedBranchIds:", selectedBranchIds);

      // Initialize data from item - with validation
      useEffect(() => {
        if (item.data) {
          if (Array.isArray(item.data) && item.data.length > 0) {
            setChartData([...item.data]);
          } else {
            // Set empty array for invalid data structure
            setChartData([]);
          }
        } else {
          setChartData([]);
        }
      }, [item.data]);

      // Fetch saved queries based on item type
      // NEW - only fetch queries when bank/branch filtering is happening
      useEffect(() => {
        console.log("selectedBankIds render", selectedBankIds);
        const hasFilters =
          (selectedBankIds && selectedBankIds.length > 0) ||
          (selectedBranchIds && selectedBranchIds.length > 0);

        // Only fetch queries if we have filters AND haven't fetched yet
        if (hasFilters) {
          const fetchSavedQueries = async () => {
            try {
              setLoadingQueries(true);
              setQueryError(null);
              console.log("Fetching saved queries for CHART type");

              const response = await getData(
                getQueriesListByWidgetType.replace("WIDGETTYPE", "CHART")
              );

              console.log("Raw saved queries response:", response);

              const parsed = response.map((queryItem) => ({
                ...queryItem,
                queryBuilder:
                  typeof queryItem.queryBuilder === "string"
                    ? JSON.parse(queryItem.queryBuilder)
                    : queryItem.queryBuilder,
              }));

              console.log("Parsed saved queries:", parsed);
              setSavedQueries(parsed);
            } catch (err) {
              console.error("Error fetching saved queries:", err);
              setQueryError("Failed to load saved queries");
            } finally {
              setLoadingQueries(false);
            }
          };

          fetchSavedQueries();
        }
      }, [selectedBankIds, selectedBranchIds]);

      // Transform NiFi response to chart data format
      const transformNiFiDataToChartFormat = (nifiResponse) => {
        try {
          console.log("Raw NiFi response:", nifiResponse);

          if (
            nifiResponse &&
            nifiResponse.status === "success" &&
            nifiResponse.data
          ) {
            const { data } = nifiResponse;

            // If data is already an array of objects, use it directly
            if (
              Array.isArray(data) &&
              data.length > 0 &&
              typeof data[0] === "object"
            ) {
              console.log("Data is already in object format:", data);
              return data;
            }

            // If data is array of arrays with columns
            if (
              nifiResponse.columns &&
              Array.isArray(data) &&
              Array.isArray(data[0])
            ) {
              const { columns } = nifiResponse;
              const transformedData = data.map((row) => {
                const dataPoint = {};
                columns.forEach((column, index) => {
                  let cleanKey = column
                    .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                    .replace(/\)$/, "")
                    .replace(/^.*\./, "");
                  dataPoint[cleanKey] = row[index];
                });
                return dataPoint;
              });
              console.log("Transformed from array of arrays:", transformedData);
              return transformedData;
            }
          }

          console.log("No valid data found, returning empty array");
          return [];
        } catch (error) {
          console.error("Error transforming NiFi data:", error);
          return [];
        }
      };

      // Function to fetch filtered data from NiFi
      const fetchFilteredData = async (bankIds, branchIds) => {
        try {
          if (!item?.dataSource || !savedQueries || savedQueries.length === 0) {
            console.log("Chart: No data source or saved queries available");
            return Array.isArray(item?.data) ? item.data : [];
          }

          // Try different widget ID patterns to find a match
          const possibleWidgetIds = [
            `WIDGET_${item.id}`,
            `widget_${item.id}`,
            `${item.id}`,
            item.dataSource,
          ];

          let matchedQuery = null;
          for (const widgetId of possibleWidgetIds) {
            matchedQuery = savedQueries.find(
              (q) => q.dashboardWidgetId === widgetId
            );
            if (matchedQuery) {
              console.log(`Found matching query with widgetId: ${widgetId}`);
              break;
            }
          }

          console.log("Chart: All possible widget IDs:", possibleWidgetIds);
          console.log(
            "Chart: Available saved queries:",
            savedQueries.map((q) => ({
              dashboardWidgetId: q.dashboardWidgetId,
              id: q.id,
            }))
          );

          if (!matchedQuery) {
            console.log(
              "Chart: No matching query found for any widget ID pattern"
            );
            return Array.isArray(item?.data) ? item.data : [];
          }

          console.log("Chart: Found matching query:", matchedQuery);

          // Get query object
          const queryObject =
            typeof matchedQuery.queryBuilder === "string"
              ? JSON.parse(matchedQuery.queryBuilder)
              : matchedQuery.queryBuilder;

          // Extract table name from entity
          const entity = queryObject.entity || "";
          const tableName = entity.includes(".")
            ? entity.split(".")[1]
            : entity;

          // Get joins information
          const joins = queryObject.joins || [];
          const joinedTables = joins.map((join) => {
            const table = join.table || join.alias || "";
            return table.includes(".") ? table.split(".")[1] : table;
          });

          // Helper function to determine filter field names
          const determineFilterFields = () => {
            let bankFilterField = null;
            let branchFilterField = null;

            console.log("Chart: Table name:", tableName);

            // FIRST CHECK: For any table that is NOT "bank" or "branch_details", use tableName.bank_id and tableName.branch_id
            if (tableName !== "bank" && tableName !== "branch_details") {
              bankFilterField = `${tableName}.bank_id`;
              branchFilterField = `${tableName}.branch_id`;
              return { bankFilterField, branchFilterField };
            }

            // If the first condition doesn't match, proceed with existing join logic
            // Check what tables are available
            const hasBankTable =
              tableName === "bank" || joinedTables.includes("bank");
            const hasBranchDetailsTable =
              tableName === "branch_details" ||
              joinedTables.includes("branch_details");
            const hasJoins = joins.length > 0;

            console.log("Chart: Table analysis:", {
              tableName,
              joinedTables,
              hasBankTable,
              hasBranchDetailsTable,
              hasJoins,
            });

            // Determine bank filter field
            if (tableName === "bank") {
              // If entity is bank table
              bankFilterField = "bank.id";
            } else if (hasBankTable && hasJoins) {
              // If joins are there and bank table is available
              bankFilterField = "bank.id";
            } else if (
              !hasBankTable &&
              tableName !== "bank" &&
              tableName !== "branch_details"
            ) {
              // For core tables (not bank or branch_details), use table.bank_id
              bankFilterField = `${tableName}.bank_id`;
            } else if (!hasBankTable) {
              // If no bank table available, use current table with bank_id
              bankFilterField = `${tableName}.bank_id`;
            }

            // Determine branch filter field
            if (tableName === "branch_details") {
              // If entity is branch_details table
              branchFilterField = "branch_details.id";
            } else if (hasBranchDetailsTable && hasJoins) {
              // If joins are there and branch_details table is available
              branchFilterField = "branch_details.id";
            } else if (
              !hasBranchDetailsTable &&
              tableName !== "bank" &&
              tableName !== "branch_details"
            ) {
              // For core tables (not bank or branch_details), use table.branch_id
              branchFilterField = `${tableName}.branch_id`;
            } else {
              // Check joined tables for branch_id field
              const tableWithBranchId = joinedTables.find(
                (table) => table !== "bank" && table !== "branch_details"
              );
              if (tableWithBranchId) {
                branchFilterField = `${tableWithBranchId}.branch_id`;
              } else {
                branchFilterField = `${tableName}.branch_id`;
              }
            }

            return { bankFilterField, branchFilterField };
          };

          const { bankFilterField, branchFilterField } =
            determineFilterFields();

          console.log("Chart: Determined filter fields:", {
            bankFilterField,
            branchFilterField,
          });

          const filters = {};

          // Add bank filter if bankIds provided and field determined
          if (bankIds && bankIds.length > 0 && bankFilterField) {
            filters[bankFilterField] = {
              operator: "IN",
              value: bankIds,
            };
          }

          // Add branch filter if branchIds provided and field determined
          if (branchIds && branchIds.length > 0 && branchFilterField) {
            filters[branchFilterField] = {
              operator: "IN",
              value: branchIds,
            };
          } else if (!branchIds || branchIds.length === 0) {
            // Explicitly send null when branchIds is not available
            if (branchFilterField) {
              filters[branchFilterField] = null;
            }
          }

          console.log("Chart: Original query object:", queryObject);
          console.log("Chart: Applied filters:", filters);

          const enhancedQuery = {
            ...queryObject,
            filters: {
              ...(queryObject.filters || {}),
              ...filters,
            },
          };

          console.log(
            "Chart: Enhanced query with dynamic filters:",
            JSON.stringify(enhancedQuery, null, 2)
          );

          const response = await fetch("/nifi/fetchQuery", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(enhancedQuery),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log("Chart: NiFi Query Result:", result);

          if (result.status === "error") {
            throw new Error(result.message || "Query execution failed");
          }

          // Transform NiFi response to chart format
          if (result.status === "success" && result.data) {
            const transformedData = transformNiFiDataToChartFormat(result);
            console.log("Chart: Final transformed data:", transformedData);
            return transformedData;
          }

          return [];
        } catch (error) {
          console.error("Chart: Error fetching filtered data:", error);
          // Return original data on error
          return Array.isArray(item?.data) ? item.data : [];
        }
      };

      // Effect to handle data fetching when filters change
      useEffect(() => {
        const loadData = async () => {
          console.log("Chart: LoadData called with:", {
            selectedBankIds,
            selectedBranchIds,
            hasDataSource: !!item?.dataSource,
            savedQueriesLength: savedQueries.length,
            loadingQueries,
            itemId: item.id,
          });

          // Check if any filters are applied
          const hasFilters =
            (selectedBankIds && selectedBankIds.length > 0) ||
            (selectedBranchIds && selectedBranchIds.length > 0);

          if (hasFilters) {
            setLoading(true);
            try {
              console.log(
                "Chart: Filters applied, attempting to fetch filtered data"
              );

              const fetchedData = await fetchFilteredData(
                selectedBankIds,
                selectedBranchIds
              );

              console.log("Chart: Setting chart data to:", fetchedData);
              setChartData([...fetchedData]);
            } catch (error) {
              console.error("Chart: Error loading filtered data:", error);
              setChartData([...(item.data || [])]);
            } finally {
              setLoading(false);
            }
          } else {
            console.log("Chart: No filters applied, using original data");
            setChartData([...(item.data || [])]);
          }
        };

        // Only run loadData if queries have finished loading
        if (!loadingQueries) {
          loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        selectedBankIds,
        selectedBranchIds,
        item.dataSource,
        loadingQueries,
        savedQueries.length,
        item.id,
      ]);

      // Debug useEffect to track chartData changes
      useEffect(() => {
        console.log("Chart data state updated:", {
          length: chartData.length,
          firstItem: chartData[0],
          timestamp: new Date().toISOString(),
        });
      }, [chartData]);

      // Show loading state for queries or data
      if (loading || loadingQueries) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#6b7280",
              fontSize: "14px",
            }}
          >
            {loadingQueries ? "Loading queries..." : "Loading chart data..."}
          </div>
        );
      }

      // Show error state if queries failed to load
      if (queryError) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#ef4444",
              fontSize: "14px",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                padding: "16px",
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #ef4444",
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                Error Loading Queries
              </div>
              <div style={{ fontSize: "12px" }}>{queryError}</div>
            </div>
          </div>
        );
      }

      // ENHANCED: Show no data message for both initial and filtered states
      if (chartData.length === 0 && !loading && !loadingQueries) {
        // const hasSelectedBranches =
        //   selectedBranchIds && selectedBranchIds.length > 0;
        // const hasSelectedBanks = selectedBankIds && selectedBankIds.length > 0;
        // const hasFilters = hasSelectedBranches || hasSelectedBanks;

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#6b7280",
              fontSize: "14px",
              textAlign: "center",
              padding: "20px",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{
                padding: "10px",
                backgroundColor: "#fef3c7",
                borderRadius: "8px",
                border: "1px solid #f59e0b",
              }}
            >
              <div
                style={{
                  color: "#92400e",
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                No Data Available
              </div>
            </div>
          </div>
        );
      }

      const containerWidth = containerRef.current?.offsetWidth || 300;
      const containerHeight = containerRef.current?.offsetHeight || 300;

      // Format date for display
      const formatDateForDisplay = (dateString) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return dateString;
          const day = date.getDate().toString().padStart(2, "0");
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        } catch (error) {
          return dateString;
        }
      };

      // Transform data to format dates for display
      const formattedData = chartData.map((dataItem) => {
        const formattedItem = { ...dataItem };
        Object.keys(formattedItem).forEach((key) => {
          const value = formattedItem[key];
          if (
            key.toLowerCase().includes("date") ||
            (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value))
          ) {
            formattedItem[key] = formatDateForDisplay(value);
          }
        });
        return formattedItem;
      });

      const chartColors = [
        "#1a7a73",
        "#10B981",
        "#8B5CF6",
        "#EF4444",
        "#F59E0B",
        "#EC4899",
        "#14B8A6",
        "#84CC16",
      ];

      // Create a unique key that includes data hash to force re-render
      const dataHash = JSON.stringify(chartData).substring(0, 50);
      const chartKey = `${item.id}-${chartData.length}-${dataHash}`;

      console.log("Chart: Rendering with:", {
        chartType: item.chartType,
        dataLength: chartData.length,
        formattedDataLength: formattedData.length,
        chartKey,
        sampleData: chartData[0],
        savedQueriesCount: savedQueries.length,
      });

      // Render the appropriate chart component
      return (
        <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
          {(() => {
            switch (item.chartType) {
              case "radialProgress":
                return (
                  <RadialProgressChart
                    key={chartKey}
                    data={chartData}
                    colors={item.colors}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    title={item.title}
                    containerWidth={containerWidth}
                    containerHeight={containerHeight}
                    item={item}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "line":
                return (
                  <LineChartComponent
                    key={chartKey}
                    formattedData={formattedData}
                    item={item}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    yAxisLabels={item.yAxisLabels}
                    colors={item.colors}
                    isExpanded={isExpanded}
                    isViewMode={isViewMode}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "progressBar":
                return (
                  <ProgressBarChart
                    key={chartKey}
                    data={chartData}
                    item={item}
                    heightExpandLevel={item.heightExpandLevel || 1}
                    yAxisLabels={item.yAxisLabels}
                    yAxisKeys={item.yAxisKeys}
                    colors={item.colors}
                    xAxisKey={item.xAxisKey}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "area":
                return (
                  <AreaChartJS
                    key={chartKey}
                    formattedData={formattedData}
                    isExpanded={isExpanded}
                    item={item}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    yAxisLabels={item.yAxisLabels}
                    colors={item.colors}
                    isViewMode={isViewMode}
                    data={chartData}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "pie":
                return (
                  <PieChartJS
                    key={chartKey}
                    formattedData={formattedData}
                    isExpanded={isExpanded}
                    item={item}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    chartColors={chartColors}
                    data={chartData}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "bar":
                return (
                  <BarChart
                    key={chartKey}
                    data={chartData}
                    formattedData={formattedData}
                    item={item}
                    isExpanded={isExpanded}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    colors={item.colors}
                    isViewMode={isViewMode}
                    yAxisLabels={item.yAxisLabels}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "radar":
                return (
                  <RadarChartComponent
                    key={chartKey}
                    data={formattedData}
                    colors={item.colors}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    yAxisLabels={item.yAxisLabels}
                    isExpanded={isExpanded}
                    containerWidth={containerWidth}
                    containerHeight={containerHeight}
                    item={item}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "composed":
                return (
                  <ComposedChartComponent
                    key={chartKey}
                    formattedData={formattedData}
                    data={chartData}
                    item={item}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    yAxisLabels={item.yAxisLabels}
                    colors={item.colors}
                    chartColors={chartColors}
                    isExpanded={isExpanded}
                    isViewMode={isViewMode}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "stackedBar":
                return (
                  <StackedBarChartComponent
                    key={chartKey}
                    formattedData={formattedData}
                    item={item}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    yAxisLabels={item.yAxisLabels}
                    colors={item.colors}
                    isExpanded={isExpanded}
                    isViewMode={isViewMode}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              case "trend":
                return (
                  <TrendChart
                    key={chartKey}
                    data={formattedData}
                    xAxisKey={item.xAxisKey}
                    yAxisKeys={item.yAxisKeys}
                    colors={item.colors}
                    yAxisLabels={item.yAxisLabels}
                    isViewMode={isViewMode}
                    item={item}
                    selectedBankIds={selectedBankIds}
                    selectedBranchIds={selectedBranchIds}
                    editConfigIds={editConfigId}
                  />
                );
              default:
                return null;
            }
          })()}
        </div>
      );
    };

    // CONVERTED: renderTableContent is now a regular function using parent state
    const renderTableContent = (item) => {
      console.log("TableRenderer - selectedBankIds:", selectedBankIds);
      console.log("TableRenderer - selectedBranchIds:", selectedBranchIds);

      // Show loading state using parent component state
      if (tableLoadingQueries) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#6b7280",
              fontSize: "14px",
            }}
          >
            Loading...
          </div>
        );
      }

      // Show error state using parent component state
      if (tableQueryError) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#ef4444",
              fontSize: "14px",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                padding: "16px",
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #ef4444",
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                Error Loading Queries
              </div>
              <div style={{ fontSize: "12px" }}>{tableQueryError}</div>
            </div>
          </div>
        );
      }

      let {
        data = [],
        columns = [],
        subColumns = [],
        showDownload = false,
        showPDFDownload = false,
        enablePDFTitle = false,
        // showBankLogo = false,
        showSearch = false,
        showPagination = false,
        enableSorting = false,
        tableType = "basic",
        title = "",
        showDateFilter = false,
      } = item;

      console.log("Table item data:", item);
      console.log("Table selectedBankIds:", selectedBankIds);
      console.log("Table selectedBranchIds:", selectedBranchIds);

      // ✅ ONLY apply local filtering if there's NO dataSource (NiFi)
      // If there's a dataSource, let TableComponent handle the filtering via fetchFilteredData
      if (
        !item?.dataSource &&
        (selectedBankIds?.length > 0 || selectedBranchIds?.length > 0) &&
        data.length > 0
      ) {
        const hasBankId = data.some(
          (entry) =>
            entry.hasOwnProperty("bank_id") ||
            entry.hasOwnProperty("bankId") ||
            entry.hasOwnProperty("Bank_ID")
        );

        const hasBranchId = data.some(
          (entry) =>
            entry.hasOwnProperty("branchdetails_id") ||
            entry.hasOwnProperty("branch_id") ||
            entry.hasOwnProperty("branchId") ||
            entry.hasOwnProperty("Branch_ID")
        );

        // Filter by branch IDs first (more specific)
        if (selectedBranchIds?.length > 0 && hasBranchId) {
          data = data.filter((entry) => {
            const branchId =
              entry.branchdetails_id ||
              entry.branch_id ||
              entry.branchId ||
              entry.Branch_ID;
            return selectedBranchIds.includes(branchId);
          });
        }
        // If no branch filter, apply bank filter
        else if (selectedBankIds?.length > 0 && hasBankId) {
          data = data.filter((entry) => {
            const bankId = entry.bank_id || entry.bankId || entry.Bank_ID;
            return selectedBankIds.includes(bankId);
          });
        }

        // ✅ ONLY show no data message for local filtering (no dataSource)
        if (data.length === 0) {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#6b7280",
                fontSize: "14px",
                textAlign: "center",
                padding: "20px",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#fef3c7",
                  borderRadius: "8px",
                  border: "1px solid #f59e0b",
                }}
              >
                <div
                  style={{
                    color: "#92400e",
                    fontWeight: "600",
                    marginBottom: "4px",
                  }}
                >
                  No Data Available
                </div>
                <div style={{ color: "#d97706", fontSize: "12px" }}>
                  {selectedBranchIds?.length > 0
                    ? `No data found for the selected ${
                        selectedBranchIds.length > 1 ? "Branches" : "Branch"
                      }`
                    : `No data found for the selected ${
                        selectedBankIds?.length > 1 ? "Banks" : "Bank"
                      }`}
                </div>
              </div>
            </div>
          );
        }
      }

      // ✅ Always call TableComponent - let it handle NiFi data fetching and its own no-data states
      return (
        <TableComponent
          data={data}
          columns={columns}
          subColumns={subColumns}
          showDownload={showDownload}
          showPDFDownload={showPDFDownload}
          enablePDFTitle={enablePDFTitle}
          showSearch={showSearch}
          showPagination={showPagination}
          enableSorting={enableSorting}
          tableType={tableType}
          title={title}
          showDateFilter={showDateFilter}
          item={item}
          tablePagination={tablePagination}
          tableRowsPerPage={tableRowsPerPage}
          onTablePageChange={onTablePageChange}
          onTableRowsPerPageChange={onTableRowsPerPageChange}
          expandLevel={expandLevel}
          selectedBankIds={selectedBankIds}
          selectedBranchIds={selectedBranchIds}
          savedQueries={tableSavedQueries} // Using parent component state
          selectedDateField={item.selectedDateField}
          editConfigIds={editConfigId}
        />
      );
    };

    const renderMap = (item) => {
      return (
        <Map
          item={item}
          selectedBankIds={selectedBankIds}
          selectedBranchIds={selectedBranchIds}
          uploadedIcons={uploadedIcons}
          iconMap={iconMap}
          iconSizes={iconSizes}
          attachmentList={attachmentList}
          isViewMode={isViewMode}
        />
      );
    };

    const renderCardContent = (item, containerWidth = 400) => {
      return (
        <CardRenderer
          item={item}
          containerWidth={containerWidth}
          selectedBankIds={selectedBankIds}
          selectedBranchIds={selectedBranchIds}
          // Pass parent component state as props
          savedQueries={cardSavedQueries}
          loadingQueries={cardLoadingQueries}
          queryError={cardQueryError}
        />
      );
    };

    // CONVERTED: CardRenderer is now a regular function using passed props instead of hooks
    const CardRenderer = ({
      item,
      containerWidth = 400,
      selectedBankIds,
      selectedBranchIds,
      savedQueries, // Receive from parent
      loadingQueries, // Receive from parent
      queryError, // Receive from parent
    }) => {
      console.log("Card selectedBankIds", selectedBankIds);

      // Show loading state using passed props
      if (loadingQueries) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#6b7280",
              fontSize: "14px",
            }}
          >
            Loading queries...
          </div>
        );
      }

      // Show error state using passed props
      if (queryError) {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#ef4444",
              fontSize: "14px",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <div
              style={{
                padding: "16px",
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
                border: "1px solid #ef4444",
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                Error Loading Queries
              </div>
              <div style={{ fontSize: "12px" }}>{queryError}</div>
            </div>
          </div>
        );
      }

      return (
        <CardComponent
          item={item}
          containerWidth={containerWidth}
          selectedBankIds={selectedBankIds}
          selectedBranchIds={selectedBranchIds}
          savedQueries={savedQueries} // Using passed prop
        />
      );
    };

    const renderHOBankList = (item, containerWidth = 400) => {
      console.log("item DATAAAAAAAAAAAAAAA", item);

      let { data, title } = item;

      return (
        <TreeListComponent
          data={data}
          title={title}
          containerWidth={containerWidth}
          cardStyles={item.cardStyles || {}}
          fieldsConfig={item.fieldsConfig || []}
          selectedBankId={selectedBankId}
          selectedBankIds={selectedBankIds}
          selectedBranchId={selectedBranchId}
          selectedBranchIds={selectedBranchIds}
          expandedBanks={expandedBanks}
          onBankSelect={onBankSelect}
          onBranchSelect={onBranchSelect}
          handleMultiBankSelect={handleMultiBankSelect}
          handleMultiBranchSelect={handleMultiBranchSelect}
          setExpandedBanks={setExpandedBanks}
          isMultiSelectionMode={true}
          getThemeURL={getThemeURL}
          getData={getData}
        />
      );
    };

    if (item.type === "card") {
      return (
        <div
          ref={ref}
          className="grid-stack-item"
          gs-id={item.id}
          gs-x={item.x || 0}
          gs-y={item.y || 0}
          gs-w={item.w || 3}
          gs-h={item.h || 2}
          style={{ boxSizing: "border-box", padding: "5px" }}
        >
          <div
            className="grid-stack-item-content"
            style={{
              cursor: "move",
              position: "relative",
              background: item.color,
              color: item.textColor || "white",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              width: "100%",
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  fontWeight: "600",
                  fontSize: `${item.title_fontSize || 16}px`,
                  lineHeight: 1.2,
                  marginTop: "6px",
                  color: item.title_color || "black",
                  textAlign: item.title_textAlignment || "left",
                  flex: 1,
                }}
              >
                {item.title}
              </div>

              {!isViewMode && (
                <div style={{ display: "flex", gap: "6px" }}>
                  {isEditingLayout && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(item.id);
                      }}
                      style={{
                        padding: "4px",
                        backgroundColor: "#f3f4f6",
                        border: "none",
                        borderRadius: "4px",
                        color: "#6b7280",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.backgroundColor = "#e5e7eb")
                      }
                      onMouseOut={(e) =>
                        (e.target.style.backgroundColor = "#f3f4f6")
                      }
                    >
                      <PlusCircle size={14} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(item.id, "openEditPopup");
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>

            <div
              style={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {renderCardContent(item)}
            </div>
          </div>
        </div>
      );
    } else if (item.type === "table") {
      return (
        <div
          ref={ref}
          className="grid-stack-item"
          gs-id={item.id}
          gs-x={item.x || 0}
          gs-y={item.y || 0}
          gs-w={item.w || 3}
          gs-h={item.h || 2}
          style={{ boxSizing: "border-box", padding: "5px" }}
        >
          <div
            className="grid-stack-item-content"
            style={{
              cursor: "move",
              position: "relative",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              width: "100%",
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                opacity: 0.5,
                transition: "opacity 0.2s",
                cursor: "move",
              }}
            >
              <Move size={16} />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: 1,
                }}
              >
                <Table size={20} color="#111827" />
                <h3
                  style={{
                    fontSize: `${item.title_fontSize || 16}px`,
                    fontWeight: "600",
                    color: item.title_color || "#111827",
                    textAlign: item.title_textAlignment || "left",
                    transition: "color 0.2s",
                    width: "100%",
                    margin: 0,
                    display: "block",
                  }}
                >
                  {item.title}
                </h3>
              </div>
              {!isViewMode && (
                <div style={{ display: "flex", gap: "6px" }}>
                  {isEditingLayout && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(item.id);
                      }}
                      style={{
                        padding: "4px",
                        backgroundColor: "#f3f4f6",
                        border: "none",
                        borderRadius: "4px",
                        color: "#6b7280",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.backgroundColor = "#e5e7eb")
                      }
                      onMouseOut={(e) =>
                        (e.target.style.backgroundColor = "#f3f4f6")
                      }
                    >
                      <PlusCircle size={14} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(item.id, "openEditPopup");
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>
            <div
              style={{
                flex: 1,
                overflow: "hidden",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {renderTableContent(item)}
            </div>
          </div>
        </div>
      );
    } else if (item.type === "map") {
      return (
        <div
          ref={ref}
          className="grid-stack-item"
          gs-id={item.id}
          gs-x={item.x || 0}
          gs-y={item.y || 0}
          gs-w={item.w || 3}
          gs-h={item.h || 2}
          style={{
            boxSizing: "border-box",
            padding: "5px",
          }}
        >
          <div
            className="grid-stack-item-content"
            style={{
              cursor: "move",
              position: "relative",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              width: "100%",
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: 1,
                }}
              >
                <MapPin size={20} color="#111827" />
                <h3
                  style={{
                    fontSize: `${item.title_fontSize || 16}px`,
                    fontWeight: "600",
                    color: item.title_color || "#111827",
                    textAlign: item.title_textAlignment || "left",
                    transition: "color 0.2s",
                    width: "100%",
                    margin: 0,
                    display: "block",
                  }}
                  onClick={() => setEditingId(item.id)}
                >
                  {item.title}
                </h3>
              </div>
              {!isViewMode && (
                <div style={{ display: "flex", gap: "6px" }}>
                  {isEditingLayout && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(item.id);
                      }}
                      style={{
                        padding: "4px",
                        backgroundColor: "#f3f4f6",
                        border: "none",
                        borderRadius: "4px",
                        color: "#6b7280",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.backgroundColor = "#e5e7eb")
                      }
                      onMouseOut={(e) =>
                        (e.target.style.backgroundColor = "#f3f4f6")
                      }
                    >
                      <PlusCircle size={14} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(item.id, "openEditPopup");
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflow: "hidden" }}>{renderMap(item)}</div>
          </div>
        </div>
      );
    } else if (item.type === "chart") {
      return (
        <div
          ref={ref}
          className="grid-stack-item"
          gs-id={item.id}
          gs-x={item.x || 0}
          gs-y={item.y || 0}
          gs-w={item.w || 3}
          gs-h={item.h || 2}
          style={{
            boxSizing: "border-box",
            padding: "5px",
          }}
        >
          <div
            className="grid-stack-item-content"
            style={{
              cursor: "move",
              position: "relative",
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              width: "100%",
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: 1,
                }}
              >
                {item.chartType === "line" && (
                  <LineChartIcon size={20} color="#111827" />
                )}
                {item.chartType === "area" && (
                  <AreaChartIcon size={20} color="#111827" />
                )}
                {item.chartType === "bar" && (
                  <BarChart3 size={20} color="#111827" />
                )}
                {item.chartType === "pie" && (
                  <PieChartIcon size={20} color="#111827" />
                )}
                {item.chartType === "scatter" && (
                  <Scatter size={20} color="#111827" />
                )}
                {item.chartType === "composed" && (
                  <BarChart4 size={24} color="#111827" />
                )}
                {item.chartType === "stackedBar" && (
                  <ChartBarStacked size={24} color="#111827" />
                )}
                {item.chartType === "trend" && (
                  <TrendingUp size={24} color="#111827" />
                )}
                {item.chartType === "progressBar" && (
                  <ChartBarStacked size={24} color="#111827" />
                )}
                {item.chartType === "radialProgress" && (
                  <CircleDashed size={24} color="#111827" />
                )}
                {item.chartType === "radar" && (
                  <RadarIcon size={20} color="#111827" />
                )}
                {item.chartType === "funnel" && (
                  <Funnel size={20} color="#111827" />
                )}
                <h3
                  style={{
                    fontSize: `${item.title_fontSize || 16}px`,
                    fontWeight: "600",
                    color: item.title_color || "#111827",
                    textAlign: item.title_textAlignment || "left",
                    transition: "color 0.2s",
                    width: "100%",
                    margin: 0,
                    display: "block",
                  }}
                  onClick={() => setEditingId(item.id)}
                >
                  {item.title}
                </h3>
              </div>
              {!isViewMode && (
                <div style={{ display: "flex", gap: "6px" }}>
                  {isEditingLayout && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(item.id);
                      }}
                      style={{
                        padding: "4px",
                        backgroundColor: "#f3f4f6",
                        border: "none",
                        borderRadius: "4px",
                        color: "#6b7280",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.backgroundColor = "#e5e7eb")
                      }
                      onMouseOut={(e) =>
                        (e.target.style.backgroundColor = "#f3f4f6")
                      }
                    >
                      <PlusCircle size={14} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(item.id, "openEditPopup");
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflow: "hidden" }}>
              {renderChartContent(item)}
            </div>
          </div>
        </div>
      );
    } else if (item.type === "HO_bank_list") {
      return (
        <div
          ref={ref}
          className="grid-stack-item"
          gs-id={item.id}
          gs-x={item.x || 0}
          gs-y={item.y || 0}
          gs-w={item.w || 3}
          gs-h={item.h || 2}
          style={{ boxSizing: "border-box", padding: "5px" }}
        >
          <div
            className="grid-stack-item-content"
            style={{
              cursor: "move",
              position: "relative",
              background: item.color,
              color: item.textColor || "white",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
              width: "100%",
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  fontWeight: "600",
                  fontSize: `${item.title_fontSize || 16}px`,
                  lineHeight: 1.2,
                  marginTop: "6px",
                  color: item.title_color || "black",
                  textAlign: item.title_textAlignment || "left",
                  flex: 1,
                }}
              >
                {item.title}
              </div>

              {!isViewMode && (
                <div style={{ display: "flex", gap: "6px" }}>
                  {isEditingLayout && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(item.id);
                      }}
                      style={{
                        padding: "4px",
                        backgroundColor: "#f3f4f6",
                        border: "none",
                        borderRadius: "4px",
                        color: "#6b7280",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.backgroundColor = "#e5e7eb")
                      }
                      onMouseOut={(e) =>
                        (e.target.style.backgroundColor = "#f3f4f6")
                      }
                    >
                      <PlusCircle size={14} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(item.id, "openEditPopup");
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    style={{
                      padding: "4px",
                      backgroundColor: "#f3f4f6",
                      border: "none",
                      borderRadius: "4px",
                      color: "#6b7280",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#e5e7eb")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#f3f4f6")
                    }
                  >
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>

            <div
              style={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {renderHOBankList(item)}
            </div>
          </div>
        </div>
      );
    }

    return null;
  }
);

GridStackCard.displayName = "GridStackCard";

export default GridLayoutCards;
